import '@digital-realty/ix-textbox/ix-textbox.js';
import { html, LitElement, nothing, type TemplateResult } from 'lit';
import { property } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import './ix-generic-tree-node.js';
import { IxGenericTreeNodeStyles } from './ix-generic-tree-styles.js';
import type { IGenericTreeNode } from './ix-generic-tree-types.js';
import { updateNodeCheckedStatus } from './ix-generic-tree-utils.js';
import { IxGenericTreeTestIds } from './IxGenericTreeTestIds.js';

export class IxGenericTree extends LitElement {
  static styles = [IxGenericTreeNodeStyles];

  @property({ type: Object }) rootNode!: IGenericTreeNode;

  @property({ type: Boolean }) showCheckboxes: boolean = false;

  @property({ type: Boolean }) disableCheckboxes: boolean = false;

  @property({ type: Boolean }) filledIcons: boolean = false;

  @property({ type: Boolean }) searchEnabled: boolean = false;

  @property({ type: String }) searchPlaceholder: string = '';

  @property({ type: String }) searchText: string = '';

  @property({ type: String }) selectedId?: string;

  @property({ type: Number }) searchResultCount: number = 0;

  @property({ type: Number }) searchResultIndex: number = 0;

  private onTreeNodeExpandToggle(e: CustomEvent) {
    const { node } = e.detail.message;
    node.expanded = !node.expanded;
    this.dispatchEvent(
      new CustomEvent('expand-toggle', {
        detail: {
          message: {
            ...e.detail.message,
            update: this.requestUpdate.bind(this),
          },
        },
      }),
    );
    this.requestUpdate();
  }

  private onTreeNodeCheckToggle(e: CustomEvent): void {
    const { node } = e.detail.message;
    updateNodeCheckedStatus(node, this.rootNode);
    this.rootNode = structuredClone(this.rootNode);
    this.dispatchEvent(
      new CustomEvent('check-toggle', {
        detail: {
          message: {
            ...e.detail.message,
            rootNode: this.rootNode,
          },
        },
      }),
    );
  }

  private renderNodes(
    nodes: Array<IGenericTreeNode> = [],
    level: number = 0,
  ): Array<TemplateResult<1>> {
    return repeat(
      nodes,
      node => node.id,
      node =>
        html`<ix-generic-tree-node
          id=${node.id}
          ?expanded=${!!node.expanded}
          ?loading=${!!node.loading}
          ?disabled=${this.disableCheckboxes || !!node.disabled}
          .level=${level}
          .node=${node}
          ?showCheckbox=${this.showCheckboxes}
          ?filledIcons=${this.filledIcons}
          ?searchEnabled=${this.searchEnabled}
          ?selected=${this.selectedId === node.id}
          ?searched=${node.selected}
          .searchText=${this.searchText}
          @check-toggle=${this.onTreeNodeCheckToggle}
          @expand-toggle=${this.onTreeNodeExpandToggle}
          data-testid="${IxGenericTreeTestIds.NODE_ID(node.id)}"
        >
          ${node.expanded
            ? this.renderNodes(node.children, level + 1)
            : nothing}
        </ix-generic-tree-node>`,
    ) as unknown as TemplateResult<1>[];
  }

  private renderTextSearch() {
    return this.searchEnabled
      ? html` <ix-textbox
          filled
          leading-icon="search"
          has-leading-icon
          searchEnabled
          .searchResultCount=${this.searchResultCount}
          .searchResultIndex=${this.searchResultIndex}
          placeholder=${this.searchPlaceholder}
          .value=${this.searchText}
          @input=${(e: Event) => {
            const input = e.target as HTMLInputElement;
            this.dispatchEvent(
              new CustomEvent('search-text-change', {
                detail: { searchText: input.value },
              }),
            );
          }}
          data-testid="${IxGenericTreeTestIds.SEARCH_BOX}"
        ></ix-textbox>`
      : nothing;
  }

  render() {
    return html`<div>
      ${this.renderTextSearch()} ${this.renderNodes(this.rootNode.children, 0)}
    </div>`;
  }
}
